import React, { useEffect, useState } from 'react';
import { useGame } from '../context/GameContext';
import { fetchProfile, fetchRivals, normalizeTag } from '../game/profile';
import { ACHIEVEMENTS, ACHIEVEMENT_IDS } from '../game/achievements';
import { ASCENSIONS } from '../game/ascensions';
import { CLASSES, CLASS_LIST } from '../game/classes';
import { AvatarChip } from '../components/Avatar';
import DivineCollection from '../components/DivineCollection';
import { ArrowLeft, Award, Coins, Users, Crown, Trophy, Palette } from 'lucide-react';

export default function Profile() {
  const { goToMenu, playerName, setPlayerName, profile, refreshProfile, setScreen } = useGame();
  const [rivals, setRivals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nameInput, setNameInput] = useState(playerName || '');

  useEffect(() => {
    (async () => {
      if (playerName) {
        await refreshProfile();
        const rvs = await fetchRivals(playerName);
        setRivals(rvs);
      }
      setLoading(false);
    })();
  }, [playerName]);

  const commitName = async () => {
    const n = normalizeTag(nameInput);
    if (!n) return;
    setPlayerName(n);
    await refreshProfile();
    const rvs = await fetchRivals(n);
    setRivals(rvs);
  };

  return (
    <div className="min-h-screen w-full px-6 py-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between">
        <button className="brutal-btn text-sm" onClick={goToMenu} data-testid="profile-back-button">
          <ArrowLeft size={16} /> BACK
        </button>
        <h1 className="font-display text-5xl sm:text-6xl font-black uppercase text-[color:var(--neon)]">PROFILE</h1>
        <div className="w-16" />
      </div>

      {/* v3.1: PROFILE BANNER with avatar */}
      <div
        className="mt-6 brutal-border p-5 bg-gradient-to-r from-[#0a0a0a] to-[#0a0a10] flex flex-col sm:flex-row items-center sm:items-center gap-5"
        data-testid="profile-banner"
      >
        <div className="flex items-center gap-4">
          <AvatarChip avatar={profile?.avatar} frame={profile?.equipped_frame} size={96} testid="profile-banner-avatar" />
          <div>
            <div className="text-[10px] uppercase tracking-[0.4em] text-[color:var(--text-dim)]">TAG</div>
            <div className="font-display text-4xl font-black uppercase text-[color:var(--neon)]" data-testid="profile-banner-tag">
              {profile?.tag || playerName || 'ANON_GOON'}
            </div>
            {profile?.equipped_title && (
              <div className="text-xs uppercase tracking-widest text-[color:var(--pink)] font-mono mt-1" data-testid="profile-banner-title">
                {profile.equipped_title}
              </div>
            )}
          </div>
        </div>
        <div className="flex-1"/>
        <div className="flex flex-col gap-2">
          <button
            className="brutal-btn text-xs px-3 py-2"
            onClick={() => setScreen && setScreen('avatar')}
            data-testid="profile-edit-avatar-button"
          >
            <Palette size={14} /> EDIT AVATAR
          </button>
        </div>
      </div>

      {/* Tag editor (compact) */}
      <div className="mt-4 brutal-border-dim p-3 bg-[#0a0a0a] flex items-end gap-3">
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-dim)] mb-1">CHANGE TAG</div>
          <input
            value={nameInput}
            onChange={e => setNameInput(e.target.value.slice(0, 16))}
            placeholder="ANON_GOON"
            className="w-full bg-black border-2 border-[color:var(--muted)] focus:border-[color:var(--neon)] px-3 py-2 text-sm font-mono uppercase outline-none"
            data-testid="profile-tag-input"
          />
        </div>
        <button className="brutal-btn text-xs px-3 py-2" onClick={commitName} data-testid="profile-save-tag">SAVE</button>
      </div>

      {loading && <div className="mt-6 text-center text-[color:var(--text-dim)]">Loading...</div>}

      {profile && !loading && (
        <>
          {/* Stats overview */}
          <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard icon={Coins} label="SLOP TOKENS" value={profile.slop_tokens} color="pink" testid="stat-tokens" />
            <StatCard icon={Trophy} label="WINS" value={`${profile.total_wins}/${profile.total_runs}`} color="neon" testid="stat-winrate" />
            <StatCard icon={Award} label="BEST SCORE" value={profile.best_score} color="neon" testid="stat-best-score" />
            <StatCard icon={Crown} label="BEST FLOOR" value={profile.best_floor} color="pink" testid="stat-best-floor" />
          </div>

          {/* v3.1: DIVINE OWNERSHIP */}
          <div className="mt-8">
            <div className="text-xs uppercase tracking-widest text-[color:var(--text-dim)] mb-3">DIVINE COLLECTION</div>
            <DivineCollection
              pickCounts={profile.divine_pick_counts || {}}
              playerTag={profile.tag}
              avatar={profile.avatar}
              frame={profile.equipped_frame}
              showShare={true}
            />
          </div>

          {/* Ascension progress */}
          <div className="mt-8">
            <div className="text-xs uppercase tracking-widest text-[color:var(--text-dim)] mb-3">ASCENSION PROGRESS</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {CLASS_LIST.map(cid => {
                const c = CLASSES[cid];
                const lvl = profile.ascension_unlocks?.[cid] ?? 0;
                return (
                  <div key={cid} className="brutal-border-dim p-4 bg-[#0a0a0a]" data-testid={`asc-progress-${cid}`}>
                    <div className="flex items-center gap-2">
                      <span className="text-2xl">{c.icon}</span>
                      <div className="font-display font-black uppercase" style={{color: c.color}}>{c.name}</div>
                    </div>
                    <div className="mt-3 flex gap-1">
                      {ASCENSIONS.map(a => (
                        <div key={a.level} className={`flex-1 h-6 border-2 grid place-items-center text-[10px] font-bold ${a.level <= lvl ? 'bg-[color:var(--neon)] text-black border-[color:var(--neon)]' : 'border-[color:var(--muted)] text-[color:var(--muted)]'}`}>
                          A{a.level}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Titles */}
          {profile.titles?.length > 0 && (
            <div className="mt-8">
              <div className="text-xs uppercase tracking-widest text-[color:var(--text-dim)] mb-3">TITLES</div>
              <div className="flex flex-wrap gap-2" data-testid="titles-list">
                {profile.titles.map((t, i) => (
                  <span key={i} className="brutal-border-pink px-3 py-1 text-xs uppercase font-bold text-[color:var(--pink)]">{t}</span>
                ))}
              </div>
            </div>
          )}

          {/* Achievements */}
          <div className="mt-8">
            <div className="text-xs uppercase tracking-widest text-[color:var(--text-dim)] mb-3">
              ACHIEVEMENTS ({profile.achievements?.length || 0}/{ACHIEVEMENT_IDS.length})
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="achievements-grid">
              {ACHIEVEMENT_IDS.map(aid => {
                const a = ACHIEVEMENTS[aid];
                const owned = profile.achievements?.includes(aid);
                return (
                  <div key={aid} className={`px-3 py-2 flex items-center gap-3 border-2 ${owned ? 'border-[color:var(--neon)] bg-[#0a1a0a]' : 'border-[color:var(--muted)] bg-[#0a0a0a] opacity-50'}`}
                    data-testid={`achievement-${aid}`}>
                    <Award className={owned ? 'text-[color:var(--neon)]' : 'text-[color:var(--muted)]'} size={18}/>
                    <div className="flex-1">
                      <div className="font-display font-black uppercase text-sm">{a.name}</div>
                      <div className="text-[10px] text-[color:var(--text-dim)]">{a.desc}</div>
                    </div>
                    <div className="text-xs uppercase font-bold text-[color:var(--pink)]">+{a.tokens}</div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Rivals */}
          <div className="mt-8 pb-10">
            <div className="text-xs uppercase tracking-widest text-[color:var(--text-dim)] mb-3 flex items-center gap-2">
              <Users size={14}/> RIVALS (people who ran your seeds)
            </div>
            {rivals.length === 0 && (
              <div className="text-center py-6 brutal-border-dim text-[color:var(--text-dim)] uppercase text-sm">NO RIVALS YET. SPREAD THE SLOP.</div>
            )}
            <div className="flex flex-col gap-2" data-testid="rivals-list">
              {rivals.map((r, i) => (
                <div key={i} className="flex items-center justify-between px-3 py-2 brutal-border-dim bg-[#0a0a0a]"
                  data-testid={`rival-${i}`}>
                  <div className="flex items-center gap-4">
                    <div className="font-display font-black uppercase">{r.tag}</div>
                    <div className="text-xs text-[color:var(--text-dim)]">{r.shared_seeds} SHARED SEEDS</div>
                  </div>
                  <div className="flex items-center gap-4 text-xs uppercase">
                    <span className="text-[color:var(--neon)]">W {r.wins}</span>
                    <span className="text-[color:var(--red)]">L {r.losses}</span>
                    <span className="text-[color:var(--pink)] font-display font-black text-lg">{r.best_score}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color, testid }) {
  const cls = color === 'pink' ? 'text-[color:var(--pink)]' : 'text-[color:var(--neon)]';
  return (
    <div className="brutal-border p-4 bg-[#0a0a0a]">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[color:var(--text-dim)]">
        <Icon size={12}/> {label}
      </div>
      <div className={`font-display text-3xl font-black mt-1 ${cls}`} data-testid={testid}>{value}</div>
    </div>
  );
}
